import axios from "const/axios";
import { API_URL } from "const/constants";

const publicApiBase = (API_URL || "").replace(/\/api\/v1\/?$/, "");

export const listDocumentTemplates = async () => {
  const response = await axios.get("/documents/templates");
  return response.data;
};

export const createDocumentTemplate = async ({ name, formType, description, file }) => {
  const formData = new FormData();
  formData.append("name", name);
  if (formType) formData.append("formType", formType);
  if (description) formData.append("description", description);
  formData.append("file", file);
  const response = await axios.post("/documents/templates", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const getDocumentTemplate = async (templateId) => {
  const response = await axios.get(`/documents/templates/${templateId}`);
  return response.data;
};

export const importDocumentTemplateFields = async (templateId) => {
  const response = await axios.post(`/documents/templates/${templateId}/import-fields`);
  return response.data;
};

export const updateDocumentTemplateFields = async (templateId, scope, fields) => {
  const response = await axios.put(`/documents/templates/${templateId}/fields`, fields, {
    params: scope ? { scope } : {},
  });
  return response.data;
};

export const deleteDocumentTemplate = async (templateId) => {
  await axios.delete(`/documents/templates/${templateId}`);
};

export const updateDocumentTemplateMaraRequirement = async ({ templateId, maraRequired }) => {
  const response = await axios.patch(`/documents/templates/${templateId}/mara-requirement`, {
    maraRequired,
  });
  return response.data;
};

export const updateDocumentTemplateEncryptOnRender = async ({ templateId, encryptOnRender }) => {
  const response = await axios.put(`/documents/templates/${templateId}/encrypt-on-render`, {
    encryptOnRender,
  });
  return response.data;
};

export const updateDocumentTemplatePreserveOriginalEncryption = async ({
  templateId,
  preserveOriginalEncryption,
}) => {
  const response = await axios.put(
    `/documents/templates/${templateId}/preserve-original-encryption`,
    {
      preserveOriginalEncryption,
    }
  );
  return response.data;
};

export const uploadDocumentTemplateOriginal = async ({ templateId, file }) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`/documents/templates/${templateId}/original`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

/** dryRun reports what would happen without touching any document. */
export const reissueDocumentTemplateSignedCopies = async ({ templateId, dryRun }) => {
  const response = await axios.post(
    `/documents/templates/${templateId}/signed-copies/reissue`,
    {},
    { params: { dryRun } }
  );
  return response.data;
};

export const updateDocumentTemplateScopes = async ({ templateId, scopes }) => {
  const response = await axios.patch(`/documents/templates/${templateId}/scopes`, {
    scopes,
  });
  return response.data;
};

export const copyDocumentTemplateFields = async ({ templateId, fromScope, toScope }) => {
  const response = await axios.post(
    `/documents/templates/${templateId}/fields/copy`,
    {},
    { params: { fromScope, toScope } }
  );
  return response.data;
};

export const prepareDocumentTemplate = async ({ templateId, clientId }) => {
  const response = await axios.get(`/documents/templates/${templateId}/prepare/${clientId}`);
  return response.data;
};

export const listDocumentPackets = async (clientId) => {
  const response = await axios.get("/documents/packets", {
    params: clientId ? { clientId } : {},
  });
  return response.data;
};

export const createDocumentDraft = async (payload) => {
  const response = await axios.post("/documents/packets", payload);
  return response.data;
};

export const updateDocumentDraft = async ({ packetId, payload }) => {
  const response = await axios.put(`/documents/packets/${packetId}`, payload);
  return response.data;
};

export const sendDocumentPacket = async ({ packetId, message }) => {
  const response = await axios.post(`/documents/packets/${packetId}/send`, { message });
  return response.data;
};

export const completeAdminDocumentPacket = async ({ packetId, fieldValues }) => {
  const response = await axios.post(`/documents/packets/${packetId}/complete-admin`, {
    fieldValues,
  });
  return response.data;
};

export const openDocumentPacketPdf = async (packetId, variant = "signed") => {
  const path = { draft: "draft-pdf", legacy: "legacy-signed-pdf" }[variant] || "signed-pdf";
  const newTab = window.open("", "_blank");
  try {
    const response = await axios.get(`/documents/packets/${packetId}/${path}`, {
      responseType: "blob",
    });
    const blobUrl = window.URL.createObjectURL(response.data);
    if (newTab) {
      newTab.location.href = blobUrl;
    } else {
      window.open(blobUrl, "_blank");
    }
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
  } catch (err) {
    newTab?.close();
    throw err;
  }
};

export const getPublicDocumentInfo = async (publicToken) => {
  const response = await axios.get(`${publicApiBase}/api/public/document-sign/${publicToken}`);
  return response.data;
};

export const sendPublicDocumentOtp = async (publicToken) => {
  const response = await axios.post(
    `${publicApiBase}/api/public/document-sign/${publicToken}/otp/send`
  );
  return response.data;
};

export const verifyPublicDocumentOtp = async ({ publicToken, code }) => {
  const response = await axios.post(
    `${publicApiBase}/api/public/document-sign/${publicToken}/otp/verify`,
    { code }
  );
  return response.data;
};

export const getPublicSigningSession = async ({ publicToken, accessToken }) => {
  const response = await axios.get(
    `${publicApiBase}/api/public/document-sign/${publicToken}/session`,
    {
      params: { accessToken },
    }
  );
  return response.data;
};

export const completePublicSigning = async ({ publicToken, accessToken, signatureDataUrl }) => {
  const response = await axios.post(
    `${publicApiBase}/api/public/document-sign/${publicToken}/complete`,
    {
      accessToken,
      fieldValues: signatureDataUrl,
    }
  );
  return response.data;
};
